'use client';

import { useEffect } from 'react';

import { usePathname, useSearchParams } from 'next/navigation';

import { useAppSelector } from '@/store';

import { generateEzShipURL, getEzShipIdAlongWithRestPath, isEzShipRoute } from '../utils/urlHelpers';
import useWebAlias from './useWebalias';

type EzShipObject = { ezShipId: string; urlPath: string; generateURL: typeof generateEzShipURL };

// eslint-disable-next-line sonarjs/cognitive-complexity
export default function useEzShip(): EzShipObject {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const currentParams = new URLSearchParams(searchParams.toString());
  currentParams.delete('autoOrderId');

  const currentPath = currentParams.toString().length ? `${pathname}?${currentParams.toString()}` : pathname;

  const { webalias, urlPath } = useWebAlias();

  const { ezShipId, urlPath: ezShipUrl } = getEzShipIdAlongWithRestPath(webalias ? urlPath : currentPath);

  const isCurrentRouteEzShipFlowRoute = isEzShipRoute(pathname);

  const storedEzShipId = useAppSelector(state => state.slices.ezShipSlice.config?.ezShipId);

  useEffect(() => {
    if (isCurrentRouteEzShipFlowRoute && storedEzShipId !== undefined && storedEzShipId !== ezShipId) {
      const hasEzShipId =
        urlPath.includes(`/ezship/${storedEzShipId}`) || urlPath.includes(`autoOrderID=${storedEzShipId}`);
      if (!hasEzShipId) {
        const newUrl = generateEzShipURL(urlPath, storedEzShipId.toString());

        const current = new URLSearchParams(
          Array.from(searchParams.entries()).filter(v => v[0] && v[0]?.toLowerCase() !== 'autoorderid')
        );

        window.history.replaceState(
          null,
          '',
          `${newUrl}${
            newUrl.includes('?') ? (newUrl.match(/^\?.+/) ? '&' : '') + current.toString() : '?' + current.toString()
          }`
        );
      }
    }
  }, [ezShipId, storedEzShipId, isCurrentRouteEzShipFlowRoute, urlPath, searchParams]);

  return {
    ezShipId: isCurrentRouteEzShipFlowRoute ? (storedEzShipId ? storedEzShipId.toString() : ezShipId ?? '') : '',
    urlPath: ezShipUrl,
    generateURL: generateEzShipURL,
  };
}
